let _ = t => t,
  _t;
import { useCallback, useMemo, useRef, useEffect } from 'react';
import ProsemirrorEditor from 'hubspot-prosemirror/prosemirror/ProsemirrorEditorWithoutDnd';
import useHubSpotRTE from 'hubspot-prosemirror/prosemirror/useHubSpotRTE';
import { getPlaceholderPlugin } from 'hubspot-prosemirror/prosemirror/plugins/PlaceholderPlugin';
import { Box } from 'foundations-components/transitional/components/Box';
import { usePublishMessage } from '../data/usePublishMessage';
import { useFileUpload } from '../data/useFileUpload';
import { useTicketThread } from '../data/useTicketThread';
import { useParams } from 'react-router-dom';
import I18n from 'I18n';
import { SendIcon } from 'foundations-assets/icons/components/SendIcon';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { schema } from 'hubspot-prosemirror/prosemirror/ProsemirrorSchema';
import { EditorState } from 'hubspot-prosemirror/pm/state';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { AttachIcon } from 'foundations-assets/icons/components/AttachIcon';
import { FileUploadDisplay } from './FileUploadDisplay';
import { ACCEPTED_FILE_TYPES } from '../constants/attachments';
import styled from 'foundations-theming-specialty/utils';
import { createGlobalStyle } from 'styled-components';
import { Flex } from 'foundations-components/components/Flex';
import { FileButton } from 'foundations-components/transitional/components/FileButton';
import { RTEToolbarButton } from 'hubspot-prosemirror/prosemirror/components/StyledTextToolbarButton';
import { FileUploadError, FileUploadStatus } from '../data/types';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Link } from 'foundations-components/transitional/components/Link';
import { HorizontalScrollContainer } from './HorizontalScrollContainer';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { usePortalData } from '../data/usePortalData';
import { useTicket } from '../data/useTicket';
import { UserTicketStatusOption } from '../data/types';
import { useIsPreview } from '../hooks/useIsPreview';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { usageTracker } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const GlobalEditorStyles = createGlobalStyle(_t || (_t = _`
  .ProseMirror, [class*="DropdownCaret__StyledCaret"] {
    color: ${0} !important;
  }
`), ({
  theme
}) => theme.color['text-core-default']);
const ProsemirrorStyledEditor = styled.div.withConfig({
  displayName: "MessageComposer__ProsemirrorStyledEditor"
})(["display:flex;flex-direction:column;width:100%;height:100%;word-break:break-word;background-color:", ";color:", ";opacity:", ";"], ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => theme.color['text-core-default'], ({
  disabled
}) => disabled ? 0.6 : 1);
const FileButtonWrapper = styled.div.withConfig({
  displayName: "MessageComposer__FileButtonWrapper"
})(["display:inline-flex;flex-shrink:0;"]);
export const MessageComposer = ({
  isLoading
}) => {
  const isPreview = useIsPreview();
  const {
    ticketId,
    threadId
  } = useParams();
  const {
    lastMessageId
  } = useTicketThread(Number(ticketId), threadId || '');
  const {
    data: portalData
  } = usePortalData();
  const allowReplyOnClosedTickets = portalData === null || portalData === void 0 ? void 0 : portalData.settings.allowReplyOnClosedTickets;
  const {
    data: ticket,
    loading: ticketLoading
  } = useTicket(Number(ticketId));
  const ticketClosed = (ticket === null || ticket === void 0 ? void 0 : ticket.status) === UserTicketStatusOption.CLOSED;
  const replyDisabled = !allowReplyOnClosedTickets && (ticketLoading || ticketClosed) || isPreview;
  const [editorState, setEditorState] = useHubSpotRTE();
  const {
    files,
    uploadFiles,
    removeFile,
    clearFiles
  } = useFileUpload(Number(ticketId), threadId || '');
  const uploadFilesRef = useRef(uploadFiles);
  useEffect(() => {
    uploadFilesRef.current = uploadFiles;
  }, [uploadFiles]);
  const showNoThreadWarning = useMemo(() => {
    return files.some(file => file.error === FileUploadError.NO_THREAD);
  }, [files]);
  const showClosedTicketWarning = useMemo(() => {
    return !allowReplyOnClosedTickets && !ticketLoading && ticketClosed;
  }, [allowReplyOnClosedTickets, ticketLoading, ticketClosed]);
  const clearEditor = useCallback(() => {
    setEditorState(EditorState.create({
      schema
    }));
    clearFiles();
  }, [setEditorState, clearFiles]);
  const handleSetEditorState = useCallback(newState => {
    const wasEmpty = editorState.doc.textContent.length === 0;
    const hasContent = newState.doc.textContent.length > 0;
    if (wasEmpty && hasContent) {
      usageTracker.track('Customer Portal App', 'Threads Manipulated', {
        action: 'message-initiated',
        screen: 'ticket-details'
      });
    }
    setEditorState(newState);
  }, [editorState, setEditorState]);
  const {
    publishMessage,
    publishable: {
      isPublishable,
      unpublishableReason
    },
    isPublishingMessage,
    error: sendMessageError
  } = usePublishMessage({
    ticketId: Number(ticketId),
    threadId,
    richEditorContent: editorState,
    uploads: files,
    clearEditor
  });
  const handleConfirm = useCallback(() => {
    usageTracker.track('Customer Portal App', 'Threads Manipulated', {
      action: 'reply-to-closed-ticket',
      screen: 'ticket-details'
    });
    void publishMessage();
  }, [publishMessage]);
  const handleReject = useCallback(() => {
    usageTracker.track('Customer Portal App', 'Threads Manipulated', {
      action: 'reply-to-closed-ticket-cancelled',
      screen: 'ticket-details'
    });
  }, []);
  const {
    isOpen,
    openConfirmModal,
    confirmModalProps
  } = useConfirmModal({
    onConfirm: handleConfirm,
    onReject: handleReject
  });
  const handlePublishMessage = () => {
    if (ticketClosed && allowReplyOnClosedTickets) {
      openConfirmModal();
      return;
    }
    usageTracker.track('Customer Portal App', 'Threads Manipulated', {
      action: 'message-sent',
      screen: 'ticket-details'
    });
    void publishMessage();
  };
  const editorConfig = useMemo(() => ({
    GLOBAL_VARS: {},
    BOLD: {},
    ITALIC: {},
    UNDERLINE: {},
    CLEAR_FORMATTING: {},
    TEXT_ALIGN: {},
    LISTS: {},
    LINK: {},
    BLOCKQUOTE: {},
    FONT_STYLE: {},
    FONT_SIZE: {},
    RENDER_SUBMIT_BUTTON: {},
    FILE_DROP: {
      fileDropConfig: {
        skipNonImageUpload: true,
        onDropNonImage: (_fileData, file) => {
          return void uploadFilesRef.current(file);
        }
      }
    },
    FILE_ATTACHMENTS: {
      renderAttachmentButton: /*#__PURE__*/_jsx(FileButtonWrapper, {
        children: /*#__PURE__*/_jsx(FileButton, {
          onChange: event => {
            const selectedFiles = Array.from(event.target.files || []);
            selectedFiles.forEach(selectedFile => {
              void uploadFilesRef.current(selectedFile);
            });
          },
          disabled: !lastMessageId || isPreview,
          accept: ACCEPTED_FILE_TYPES,
          multiple: true,
          use: "unstyled",
          children: /*#__PURE__*/_jsx(Tooltip, {
            title: !lastMessageId ? I18n.text(`customer-portal-app.composerToolbar.attachmentDisabled`) : I18n.text(`customer-portal-app.composerToolbar.attachment`),
            children: /*#__PURE__*/_jsx(RTEToolbarButton, {
              disabled: !lastMessageId || isPreview,
              use: "transparent",
              "aria-label": I18n.text('customer-portal-app.composerToolbar.attachment'),
              children: /*#__PURE__*/_jsx(AttachIcon, {
                color: !lastMessageId || isPreview ? 'icon-core-disabled' : 'icon-core-default'
              })
            })
          })
        })
      })
    }
  }), [lastMessageId, isPreview]);
  const disabledConfig = useMemo(() => {
    return isPublishingMessage || replyDisabled || isLoading ? {
      isDisabled: true,
      disabledText: ''
    } : undefined;
  }, [isPublishingMessage, replyDisabled, isLoading]);
  const customPlugins = useMemo(() => {
    return [getPlaceholderPlugin(I18n.text('customer-portal-app.messageComposer.placeholder'))];
  }, []);
  useEffect(() => {
    if (sendMessageError) {
      FloatingAlertStore.addAlert({
        message: I18n.text('customer-portal-app.sendMessage.messageFailedTryAgain'),
        titleText: I18n.text('customer-portal-app.errors.error'),
        type: 'danger'
      });
    }
  }, [sendMessageError]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(GlobalEditorStyles, {}), isOpen && /*#__PURE__*/_jsx(ConfirmModal, Object.assign({
      message: /*#__PURE__*/_jsx(Text, {
        color: "text-core-default",
        typography: "heading-100",
        children: I18n.text('customer-portal-app.dialog.title')
      }),
      description: /*#__PURE__*/_jsx(Text, {
        color: "text-core-default",
        children: I18n.text('customer-portal-app.dialog.content')
      }),
      confirmLabel: I18n.text('customer-portal-app.dialog.confirmText'),
      rejectLabel: I18n.text('customer-portal-app.dialog.cancelText')
    }, confirmModalProps, {
      size: "auto"
    })), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      inlineSize: "100%"
      // @ts-expect-error Flex color prop is not currently allowed
      ,
      color: "text-core-default",
      backgroundColor: "fill-surface-recessed",
      children: [showClosedTicketWarning && /*#__PURE__*/_jsx(Alert
      // @ts-expect-error type not-permitted but required to apply theming for portals
      , {
        color: "text-alert-default",
        titleText: /*#__PURE__*/_jsx(Text, {
          typography: "heading-100",
          children: I18n.text('customer-portal-app.ticketThread.threadClosed')
        }),
        type: "warning",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.ticketThread.threadClosedMessage"
          })
        })
      }), showNoThreadWarning && /*#__PURE__*/_jsx(Alert
      // @ts-expect-error type not-permitted but required to apply theming for portals
      , {
        color: "text-alert-default",
        titleText: /*#__PURE__*/_jsx(Text, {
          typography: "heading-100",
          children: I18n.text('customer-portal-app.attachErrors.uploadError')
        }),
        type: "danger",
        children: /*#__PURE__*/_jsxs(Text, {
          typography: "body-100",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.attachmentsErrors.noThread"
          }), ' ', /*#__PURE__*/_jsx(Link, {
            onClick: clearFiles,
            color: "text-alert-default",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-portal-app.attachmentsErrors.clearAll"
            })
          })]
        })
      }), /*#__PURE__*/_jsx(Box, {
        grow: "1",
        overflow: "hidden",
        children: /*#__PURE__*/_jsx(ProsemirrorStyledEditor, {
          disabled: !!(disabledConfig !== null && disabledConfig !== void 0 && disabledConfig.isDisabled),
          "aria-disabled": !!(disabledConfig !== null && disabledConfig !== void 0 && disabledConfig.isDisabled),
          "aria-busy": !!(disabledConfig !== null && disabledConfig !== void 0 && disabledConfig.isDisabled),
          "data-test-id": "message-composer-editor",
          children: /*#__PURE__*/_jsx(ProsemirrorEditor, {
            attachmentTray: () => {
              return files.length > 0 && /*#__PURE__*/_jsx(HorizontalScrollContainer, {
                itemCount: files.length,
                children: files.sort((fileA, fileB) => Number(fileB.status === FileUploadStatus.ERROR) - Number(fileA.status === FileUploadStatus.ERROR)).map(file => /*#__PURE__*/_jsx(FileUploadDisplay, {
                  fileUpload: file,
                  handleRemove: () => removeFile(file.localId)
                }, file.localId))
              });
            },
            editorState: editorState,
            setEditorState: handleSetEditorState,
            editorConfig: editorConfig,
            customPlugins: customPlugins,
            submitButton: () => /*#__PURE__*/_jsx(Tooltip, {
              title: unpublishableReason ? I18n.text(`customer-portal-app.composerToolbar.unpublishableReason.${unpublishableReason}`) : isPublishingMessage ? I18n.text('customer-portal-app.composerToolbar.sending') : undefined,
              disabled: isPublishable && !isPublishingMessage,
              children: /*#__PURE__*/_jsxs(LoadingButton, {
                use: "primary",
                size: "small",
                onClick: handlePublishMessage,
                disabled: !isPublishable || isPublishingMessage,
                loading: isPublishingMessage,
                children: [/*#__PURE__*/_jsx(Text, {
                  typography: "detail-100",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "customer-portal-app.composerToolbar.sendButton"
                  })
                }), /*#__PURE__*/_jsx(SendIcon, {
                  size: "100"
                })]
              })
            }),
            disabledConfig: disabledConfig
          })
        })
      })]
    })]
  });
};